<template>
  <div class="nexus-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Page Title -->
      <v-row class="pa-7" justify="center">
        <h1 class="nexus-title">ADD RETURNS</h1>
      </v-row>

      <!-- Form Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="10" class="pa-0">
          <v-card elevation="0" class="nexus-card">
            <v-card-text class="pa-6">
              <!-- Loading Overlay -->
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
              </v-overlay>

              <!-- Return Form -->
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- RETURN DETAILS SECTION -->
                <div class="redesign-section-header">
                  <h2 class="redesign-section-title">RETURN DETAILS</h2>
                </div>

                <!-- Two Column Layout -->
                <v-row class="mt-4">
                  <!-- LEFT COLUMN -->
                  <v-col cols="12" md="6">
                    <!-- Return ID -->
                    <div class="field-group mb-4">
                      <div class="field-label">
                        <v-icon size="small">mdi-identifier</v-icon>
                        Return ID
                      </div>
                      <v-text-field
                        v-model="returnId"
                        outlined
                        dense
                        readonly
                        hide-details
                        class="nexus-input mt-2"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </div>

                    <!-- Customer -->
                    <div class="field-group mb-4">
                      <div class="field-label">
                        <v-icon size="small">mdi-office-building</v-icon>
                        Customer
                      </div>
                      <v-autocomplete
                        v-model="selectedCustomer"
                        :items="customers"
                        item-text="companyName"
                        item-value="docId"
                        outlined
                        dense
                        hide-details
                        class="nexus-input mt-2"
                        :rules="[rules.required]"
                        return-object
                      ></v-autocomplete>
                    </div>

                    <!-- Contact Person -->
                    <div class="field-group mb-4">
                      <div class="field-label">
                        <v-icon size="small">mdi-account</v-icon>
                        Contact Person
                      </div>
                      <v-text-field
                        v-model="contactPerson"
                        outlined
                        dense
                        hide-details
                        class="nexus-input mt-2"
                        placeholder="Enter contact person name"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </div>

                    <!-- Invoice Number -->
                    <div class="field-group">
                      <div class="field-label">
                        <v-icon size="small">mdi-file-document</v-icon>
                        Invoice Number
                      </div>
                      <v-text-field
                        v-model="invoiceNumber"
                        outlined
                        dense
                        hide-details
                        class="nexus-input mt-2"
                        placeholder="Enter invoice number"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </div>
                  </v-col>

                  <!-- RIGHT COLUMN -->
                  <v-col cols="12" md="6">
                    <!-- Received By -->
                    <div class="field-group mb-4">
                      <div class="field-label">
                        <v-icon size="small">mdi-account-check</v-icon>
                        Received By
                      </div>
                      <v-select
                        v-model="receivedBy"
                        :items="receivedByOptions"
                        outlined
                        dense
                        hide-details
                        class="nexus-input mt-2"
                        :rules="[rules.required]"
                      ></v-select>
                    </div>

                    <!-- Return Status -->
                    <div class="field-group mb-4">
                      <div class="field-label">
                        <v-icon size="small">mdi-file-edit</v-icon>
                        Return Status
                      </div>
                      <v-select
                        v-model="returnStatus"
                        :items="filteredStatusOptions"
                        outlined
                        dense
                        hide-details
                        class="nexus-input mt-2"
                      ></v-select>
                    </div>

                    <!-- Assigned To (Conditional) -->
                    <div class="field-group mb-4" v-if="showAssignedTo">
                      <div class="field-label">
                        <v-icon size="small">mdi-account-arrow-right</v-icon>
                        Assigned To
                      </div>
                      <v-select
                        v-model="inspection"
                        :items="assignedToOptions"
                        outlined
                        dense
                        hide-details
                        class="nexus-input mt-2"
                        :rules="returnStatus === 'Rejected' ? [] : [rules.required]"
                      ></v-select>
                    </div>

                    <!-- Credit Note Number (Conditional) -->
                    <div class="field-group" v-if="showCreditNote">
                      <div class="field-label">
                        <v-icon size="small">mdi-file-document-outline</v-icon>
                        Credit Note Number
                      </div>
                      <v-text-field
                        v-model="creditNoteNumber"
                        outlined
                        dense
                        hide-details
                        class="nexus-input mt-2"
                        placeholder="Enter credit note number"
                      ></v-text-field>
                    </div>
                  </v-col>
                </v-row>

                <!-- Return Reason -->
                <v-row class="mt-4">
                  <v-col cols="12">
                    <div class="reason-box">
                      <div class="reason-label">
                        <v-icon size="small" color="#475569">mdi-comment-text</v-icon>
                        Return Reason
                      </div>
                      <v-textarea
                        v-model="returnReason"
                        outlined
                        dense
                        rows="3"
                        hide-details
                        class="nexus-input mt-2"
                        placeholder="Enter reason for return..."
                        :rules="[rules.required]"
                      ></v-textarea>
                    </div>
                  </v-col>
                </v-row>

                <!-- RETURN ITEMS SECTION -->
                <div class="redesign-section-header mt-8">
                  <h2 class="redesign-section-title">RETURN ITEMS</h2>
                </div>

                <!-- Item Cards -->
                <div v-for="(returnItem, index) in returnItems" :key="index" class="item-card mt-4">
                  <!-- Item Header -->
                  <div class="item-header">
                    <div class="item-number">Item {{ index + 1 }} of {{ returnItems.length }}</div>
                    <v-btn
                      size="small"
                      variant="flat"
                      color="error"
                      @click="confirmRemoveItem(index)"
                      v-if="returnItems.length > 1"
                    >
                      <v-icon size="small" class="mr-1">mdi-close</v-icon>
                      Remove
                    </v-btn>
                  </div>

                  <!-- Item Details Grid -->
                  <v-row class="item-details">
                    <!-- Quantity -->
                    <v-col cols="6" md="1">
                      <div class="detail-label">Quantity</div>
                      <v-text-field
                        v-model="returnItem.qty"
                        outlined
                        dense
                        hide-details
                        class="nexus-input"
                        placeholder="QTY"
                        :rules="[rules.required, rules.numeric]"
                      ></v-text-field>
                    </v-col>

                    <!-- Code -->
                    <v-col cols="6" md="3">
                      <div class="detail-label">Code</div>
                      <v-text-field
                        v-model="returnItem.code"
                        outlined
                        dense
                        hide-details
                        class="nexus-input"
                        placeholder="Product code"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>

                    <!-- Description -->
                    <v-col cols="6" md="3">
                      <div class="detail-label">Description</div>
                      <v-text-field
                        v-model="returnItem.description"
                        outlined
                        dense
                        hide-details
                        class="nexus-input"
                        placeholder="Description"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>

                    <!-- Reference Number -->
                    <v-col cols="6" md="3">
                      <div class="detail-label">Reference Number</div>
                      <v-text-field
                        v-model="returnItem.refnum"
                        outlined
                        dense
                        hide-details
                        class="nexus-input"
                        placeholder="Ref #"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>

                    <!-- Status -->
                    <v-col cols="6" md="2">
                      <div class="detail-label">Status</div>
                      <v-select
                        v-model="returnItem.status"
                        :items="itemStatusOptions"
                        outlined
                        dense
                        hide-details
                        class="nexus-input"
                      ></v-select>
                    </v-col>
                  </v-row>

                  <!-- Rejection Details Box (only for rejected items) -->
                  <div v-if="returnItem.status === 'Rejected'" class="rejection-box mt-3">
                    <v-row>
                      <!-- Rejected Quantity -->
                      <v-col cols="6" md="4">
                        <div class="detail-label">
                          <v-icon size="small" color="#991b1b">mdi-alert</v-icon>
                          Rejected Quantity
                        </div>
                        <v-text-field
                          v-model="returnItem.rejectedQty"
                          outlined
                          dense
                          hide-details
                          class="nexus-input mt-2"
                          placeholder="Qty"
                        ></v-text-field>
                      </v-col>

                      <!-- Rejection Reason -->
                      <v-col cols="12" md="8">
                        <div class="detail-label">
                          <v-icon size="small" color="#991b1b">mdi-comment-alert</v-icon>
                          Rejection Reason
                        </div>
                        <v-text-field
                          v-model="returnItem.rejectionReason"
                          outlined
                          dense
                          hide-details
                          class="nexus-input mt-2"
                          placeholder="Enter rejection reason"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Photo Upload Action -->
                  <div class="action-buttons mt-4">
                    <v-btn
                      class="nexus-btn"
                      size="small"
                      @click="triggerFileInput(index)"
                    >
                      <v-icon size="small" class="mr-1">mdi-camera</v-icon>
                      Upload Photos
                    </v-btn>
                    <span v-if="returnItem.pictures && returnItem.pictures.length > 0" class="ml-3 text-caption text-grey">
                      {{ returnItem.pictures.length }} photo(s) selected
                    </span>
                    <!-- Hidden File Input -->
                    <input
                      :ref="'fileInput' + index"
                      type="file"
                      accept="image/*"
                      style="display:none"
                      multiple
                      @change="handleFileUpload($event, index)"
                    />
                  </div>
                </div>

                <!-- Bottom Actions -->
                <div class="bottom-actions mt-6">
                  <v-btn
                    class="nexus-btn-secondary"
                    @click="confirmAddItem"
                  >
                    <v-icon class="mr-2">mdi-plus-circle-outline</v-icon>
                    Add More Return Items
                  </v-btn>
                  <v-btn
                    large
                    class="nexus-btn-save"
                    @click="confirmSubmit"
                    :disabled="!valid"
                  >
                    <v-icon class="mr-2">mdi-content-save</v-icon>
                    Save
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmationDialog.visible" max-width="400px">
        <v-card>
          <v-card-title class="headline">{{ confirmationDialog.title }}</v-card-title>
          <v-card-text>{{ confirmationDialog.message }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green-darken-1" variant="text" @click="confirmationDialog.confirm">Yes</v-btn>
            <v-btn color="red-darken-1" variant="text" @click="confirmationDialog.visible = false">No</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar Notification -->
      <v-snackbar v-model="snackbar" color="success" timeout="3000">
        Return added successfully!
        <template v-slot:actions>
          <v-btn color="white" variant="text" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  getDoc,
  doc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebaseApp, db } from "../../firebaseConfig";

export default {
  data() {
    return {
      creditNoteNumber: "",
      loading: false,
      valid: false,
      returnId: "",
      returnStatus: "Booked",
      picture: null,
      customers: [],
      selectedCustomer: null,
      receivedBy: null,
      inspection: null,
      customer: "",
      contactPerson: "",
      invoiceNumber: "",
      returnReason: "",
      deliveryDate: "",
      createdDate: "",
      deliveryDateMenu: false,
      receivedByOptions: [
        "Kate Mpofu",
        "Thabo Mohlatlole",
        "Patricia Mzolo",
        "Tumi Makgotlwe",
        "Precious Mahlaba"
      ],
      returnItems: [
        {
          qty: "",
          code: "",
          description: "",
          refnum: "",
          pictures: [],
          status: "",
          rejectedQty: "",
          rejectionReason: "",
        },
      ],
      users: [],
      assignedToOptions: [],
      confirmationDialog: {
        visible: false,
        title: "",
        message: "",
        confirm: null,
      },
      snackbar: false,
      allStatusOptions: [
        "Booked",
        "Inspection",
        "Pending Credit",
        "Labels and Packaging",
        "Return to Stores",
        "Completed",
        "Rejected",
      ],
      userRole: "",
      rules: {
        required: (value) => !!value || "Required.",
        numeric: (value) => !isNaN(value) || "Must be numeric.",
      },
    };
  },
  computed: {
    filteredStatusOptions() {
      if (this.userRole === "Stores") {
        return [
          "Labels and Packaging",
          "Return to Stores",
        ];
      }
      return this.allStatusOptions;
    },
    showAssignedTo() {
      return (
        this.returnStatus !== "Completed" &&
        this.returnStatus !== "Rejected"
      );
    },
    showCreditNote() {
      return this.returnStatus !== "Booked" && this.returnStatus !== "Inspection";
    },
    itemStatusOptions() {
      return ["Approved", "Rejected"];
    },
  },
  watch: {
    returnStatus: {
      handler() {
        this.inspection = null;
        this.fetchAssignedToOptions();
      },
      immediate: true,
    },
  },
  async created() {
    this.loading = true;
    await this.fetchUsers();
    await this.getCurrentUserRole();
    await this.getReturnId();
    await this.fetchCustomers();
    this.createdDate = new Date().toISOString().substr(0, 10);
    this.loading = false;
  },
  methods: {
    async fetchUsers() {
      try {
        const usersCollection = collection(db, "users");
        const usersSnapshot = await getDocs(usersCollection);
        this.users = usersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },

    async getCurrentUserRole() {
      try {
        const auth = getAuth(firebaseApp);
        const currentUser = auth.currentUser;
        if (currentUser) {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            this.userRole = userDoc.data().role || "Guest";
          } else {
            console.warn("No such user document!");
            this.userRole = "Guest";
          }
        } else {
          console.warn("No user is signed in.");
          this.userRole = "Guest";
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
        this.userRole = "Guest";
      }
    },

    async getReturnId() {
      try {
        const returnsCollection = collection(db, "returns");
        const returnSnapshot = await getDocs(returnsCollection);

        const returnIds = returnSnapshot.docs
          .map((doc) => parseInt(doc.data().returnId, 10))
          .filter((id) => !isNaN(id));

        const highestReturnId =
          returnIds.length > 0 ? Math.max(...returnIds) : 0;

        this.returnId = highestReturnId + 1;
      } catch (error) {
        console.error("Error fetching returnId:", error);
        this.returnId = 1;
      }
    },

    triggerFileInput(index) {
      const fileInput = this.$refs[`fileInput${index}`];
      if (fileInput && fileInput[0]) {
        fileInput[0].click();
      }
    },

    async handleFileUpload(event, index) {
      const files = event.target.files;
      const storage = getStorage(firebaseApp);

      for (let file of files) {
        try {
          const storagePath = `returns/${this.returnId}/item_${index}/${file.name}`;
          const fileRef = storageRef(storage, storagePath);

          await uploadBytes(fileRef, file);

          const url = await getDownloadURL(fileRef);

          this.returnItems[index].pictures.push(url);
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
        }
      }
    },

    async fetchAssignedToOptions() {
      const mapping = {
        Booked: [
          "Cameron Johnston",
          "Carla-Jo Barry",
          "Rodney Theunissen",
        ],
        Inspection: [
          "Cameron Johnston",
          "Colin Tee",
          "Fred Norminton",
          "Jason Steyn",
          "Keith Michael",
          "Rodney Theunissen",
          "Tawanda Nkomo",
          "Thabo B Bofelo",
          "Tori Peters",
          "Tumelo Diphoko",
        ],
        "Pending Credit": [
          "Betty Gumede",
          "Cameron Johnston",
          "Carla-Jo Barry",
          "Rodney Theunissen",
        ],
        "Labels and Packaging": [
          "Kate Mpofu",
          "Precious Mahlaba",
        ],
        "Return to Stores": [
          "Jason Steyn",
          "Tumelo Diphoko",
        ],
      };

      if (mapping[this.returnStatus]) {
        this.assignedToOptions = mapping[this.returnStatus].sort();
      } else {
        this.assignedToOptions = [];
      }
    },

    async fetchCustomers() {
      try {
        const customersCollection = collection(db, "customers");
        const customersSnapshot = await getDocs(customersCollection);
        
        this.customers = customersSnapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              docId: doc.id,
              companyName: data.companyName || data["Company Name"] || data.name || "",
              contactPerson: data.contactPerson || data["Contact Person"] || "",
              customerAccount: data.customerAccount || data["Customer Account"] || "",
            };
          })
          .filter((c) => c.companyName)
          .sort((a, b) => a.companyName.localeCompare(b.companyName));
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    },

    confirmAddItem() {
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Action",
        message: "Are you sure you want to add a new return item?",
        confirm: this.addMoreReturnItems,
      };
    },

    addMoreReturnItems() {
      this.confirmationDialog.visible = false;
      this.returnItems.push({
        qty: "",
        code: "",
        description: "",
        refnum: "",
        pictures: [],
        status: "",
        rejectedQty: "",
        rejectionReason: "",
      });
    },

    confirmRemoveItem(index) {
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Deletion",
        message: "Are you sure you want to remove this return item?",
        confirm: () => this.removeReturnItem(index),
      };
    },

    removeReturnItem(index) {
      this.confirmationDialog.visible = false;
      this.returnItems.splice(index, 1);
    },

    confirmSubmit() {
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Save",
        message: "Are you sure you want to save this return?",
        confirm: this.submitForm,
      };
    },

    async submitForm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;

      try {
        const preparedReturnItems = this.returnItems.map((item) => {
          const returnItem = {
            qty: item.qty,
            code: item.code || "",
            description: item.description || "",
            refnum: item.refnum || "",
            pictures: item.pictures || [],
            status: item.status || "",
          };

          if (item.status === "Rejected") {
            returnItem.rejectedQty = item.rejectedQty;
            returnItem.rejectionReason = item.rejectionReason;
          }

          return returnItem;
        });

        const returnData = {
          returnId: this.returnId,
          returnStatus: this.returnStatus,
          receivedBy: this.receivedBy,
          inspection: this.inspection,
          assignedTo: this.inspection
            ? {
                name: this.inspection,
                uid: this.inspection,
              }
            : null,
          customer: this.selectedCustomer
            ? {
                docId: this.selectedCustomer.docId,
                companyName: this.selectedCustomer.companyName,
                contactPerson: this.selectedCustomer.contactPerson,
                customerAccount: this.selectedCustomer.customerAccount,
              }
            : {},
          contactPerson: this.contactPerson,
          invoiceNumber: this.invoiceNumber,
          creditNoteNumber: this.creditNoteNumber,
          returnReason: this.returnReason,
          deliveryDate: this.deliveryDate,
          createdDate: this.createdDate,
          returnItems: preparedReturnItems,
        };

        returnData.creditNoteNumber = this.creditNoteNumber || "";

        await addDoc(collection(db, "returns"), returnData);

        this.snackbar = true;

        this.$refs.form.reset();
        this.deliveryDate = "";
        this.createdDate = new Date().toISOString().substr(0, 10);
        this.returnItems = [
          {
            qty: "",
            code: "",
            description: "",
            refnum: "",
            pictures: [],
            status: "",
            rejectedQty: "",
            rejectionReason: "",
          },
        ];
        this.returnReason = "";
      } catch (error) {
        console.error("Error adding return: ", error);
      } finally {
        this.loading = false;
        this.confirmationDialog.visible = false;
      }
    },

    confirmBackNavigation() {
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Navigation",
        message: "Are you sure you want to go back? Unsaved changes will be lost.",
        confirm: this.goBack,
      };
    },

    goBack() {
      this.confirmationDialog.visible = false;
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

.nexus-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  padding: 20px;
  font-family: 'Inter', sans-serif;
}

.nexus-title {
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.nexus-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  margin-top: 20px;
}

.redesign-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
}

.redesign-section-title {
  color: #1e293b;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.field-group {
  background: white;
  border-radius: 10px;
  padding: 14px;
  border: 2px solid #e2e8f0;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0;
}

.nexus-input >>> .v-input__control {
  border-radius: 8px;
}

.nexus-input >>> .v-field {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
}

.nexus-input >>> .v-field:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-field--focused {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.reason-box {
  background: #f8fafc;
  border-radius: 10px;
  padding: 14px;
  border: 2px solid #e2e8f0;
}

.reason-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0;
}

.item-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.3s;
}

.item-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #cbd5e1;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.item-number {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 500;
}

.item-details {
  margin-bottom: 0;
}

.detail-label {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 500;
  margin-bottom: 6px;
}

.rejection-box {
  background: #fef2f2;
  border: 2px solid #fecaca;
  border-radius: 8px;
  padding: 12px;
}

.nexus-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
  transition: all 0.3s ease !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.nexus-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px 0 rgba(71, 85, 105, 0.4) !important;
}

.nexus-btn-secondary {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  padding: 12px 24px !important;
  box-shadow: 0 2px 8px rgba(100, 116, 139, 0.3) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.nexus-btn-secondary:hover {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(100, 116, 139, 0.4) !important;
}

.nexus-btn-save {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  font-size: 1rem !important;
  padding: 14px 32px !important;
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.3) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.nexus-btn-save:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(71, 85, 105, 0.4) !important;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.bottom-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.back-button {
  position: absolute;
  top: 80px;
  left: 24px;
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: scale(1.05);
}

.back-button .v-icon {
  color: white !important;
}

@media (max-width: 768px) {
  .nexus-title {
    font-size: 1.75rem;
  }

  .redesign-section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .item-details .v-col {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .bottom-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .bottom-actions .v-btn {
    width: 100%;
  }

  .nexus-card {
    margin: 10px;
    border-radius: 16px;
  }
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
}
</style>